import { convertToGroupTagId, ORDER_TYPE } from '@exam-management/common/utils';
import { QBankStructureStatusTag, QBankStructureTypeTag } from '@exam-management/components';
import { type QBankStructure, useSearchQBankStructuresQuery } from '@exam-management/graphql';
import { useNavigate } from '@exam-management/router';
import {
  DataGrid,
  EllipsisText,
  ErrorBox,
  Text,
  type GridCellParams,
  type GridColDef,
  NoRowsOverlay,
} from '@shared/components';
import { formatDateTime } from '@shared/utils';
import { useMemo } from 'react';

import { useQBankStructureSearchParams } from '../../hooks';
import { QBankStructureListFilterSection } from '../qbank-structure-list-filter-section';

import MoreActionButton from './more-action-button';

export default function QBankStructureList() {
  const navigate = useNavigate();
  const [
    { search, page, pageSize, field, sort, statuses, types, group },
    setQBankStructureSearchParams,
  ] = useQBankStructureSearchParams();

  const {
    data,
    loading: isLoading,
    error,
    refetch,
  } = useSearchQBankStructuresQuery({
    variables: {
      search: {
        name: search,
        status: statuses,
        type: types,
        groupTagId: convertToGroupTagId(group),
      },
      paging: {
        currentPage: page,
        pageSize: pageSize,
      },
      order: {
        field: field,
        type: ORDER_TYPE[sort],
      },
    },
    fetchPolicy: 'network-only',
  });

  const rows = data?.qbsSearchQBankStructures.qBankStructures ?? [];
  const qBankStructureCount = data?.qbsSearchQBankStructures.total ?? 0;

  const navigateToQBankStructure = (cell: GridCellParams) => {
    if (cell.field !== 'actions') {
      navigate(`/qbank-structures/:structureId`, {
        params: { structureId: cell.id.toString() },
      });
    }
  };

  const columns = useMemo<GridColDef<QBankStructure>[]>(
    () => [
      {
        field: 'code',
        headerName: 'ID',
        sortable: false,
        width: 60,
        headerAlign: 'center',
        align: 'center',
        renderCell: (cell) => {
          return <Text>{cell.value}</Text>;
        },
      },

      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        sortable: false,

        renderCell: ({ row }) => {
          return (
            <EllipsisText maxLines={2} variant='body1Semibold'>
              {row.name}
            </EllipsisText>
          );
        },
      },
      {
        field: 'groupTag',
        headerName: 'Group',
        sortable: false,
        width: 140,

        renderCell: (cell) => {
          return <EllipsisText maxLines={2}>{cell.value ? cell.value.name : '-'}</EllipsisText>;
        },
      },
      {
        field: 'type',
        headerName: 'Type',
        sortable: false,
        width: 100,
        renderCell: (cell) => {
          return <QBankStructureTypeTag type={cell.value} />;
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 128,
        sortable: false,
        renderCell: (cell) => {
          return <QBankStructureStatusTag status={cell.value} />;
        },
      },
      {
        field: 'updatedAt',
        headerName: 'Last Modified',
        width: 156,
        renderCell: (cell) => {
          return <Text>{formatDateTime(cell.value)}</Text>;
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 48,
        sortable: false,
        renderCell: (params) => {
          const { id, name } = params.row;
          return (
            <MoreActionButton
              qBankStructureId={id}
              name={name}
              onCompleted={async () => {
                await refetch();
              }}
            />
          );
        },
      },
    ],
    [],
  );

  if (error) {
    return <ErrorBox />;
  }

  return (
    <>
      <QBankStructureListFilterSection />
      <DataGrid
        columns={columns}
        rows={rows as QBankStructure[]}
        loading={isLoading}
        hideFooter={qBankStructureCount === 0 || isLoading}
        slots={{ noRowsOverlay: NoRowsOverlay }}
        disableColumnMenu
        onCellClick={navigateToQBankStructure}
        getRowHeight={() => 'auto'}
        rowSelection={false}
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          borderRadius: theme.shape.rounded(2),
          boxShadow: 'none',
          '.MuiDataGrid-cell': {
            padding: theme.spacing(3),
            paddingBottom: theme.spacing(2.75),
          },
          '.MuiDataGrid-cell:last-of-type': {
            padding: theme.spacing(1.5),
          },
          '.MuiDataGrid-columnHeader': {
            padding: theme.spacing(3),
            backgroundColor: theme.palette.grey[100],
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
          '.MuiDataGrid-row > .MuiDataGrid-withBorderColor': {
            borderColor: theme.palette.divider,
          },
          // Style override for Footer
          '> .MuiBox-root': {
            p: theme.spacing(3, 4),
            '& .MuiTypography-root': {
              color: theme.palette.text.secondary,
            },
            '& .MuiToolbar-root': {
              minHeight: 'unset',
              height: 'unset',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                m: 0,
              },
              '& .MuiTablePagination-actions': {
                ml: theme.spacing(1),
                '> button': {
                  p: theme.spacing(1),
                },
              },
            },
          },
        })}
        slotProps={{
          footer: {
            total: qBankStructureCount,
            pagination: {
              page: page,
              count: qBankStructureCount,
              rowsPerPage: pageSize,
              onPageChange: (_event, page) => {
                setQBankStructureSearchParams({
                  page: page,
                });
              },
              onRowsPerPageChange: (event) => {
                const pageSize = parseInt(event.target.value);

                return setQBankStructureSearchParams({
                  pageSize: pageSize,
                  page: 0,
                });
              },
            },
          },
        }}
        onSortModelChange={(sorting) => {
          const field = sorting[0]?.field;
          const sort = sorting[0]?.sort ?? undefined;
          setQBankStructureSearchParams({
            field: field,
            sort: sort,
          });
        }}
        initialState={{
          sorting: {
            sortModel: [{ field, sort }],
          },
        }}
      />
    </>
  );
}
